import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, View, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../styles/theme';

interface PrimaryButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Feather icon rendered after the label. Defaults to arrow-right. */
  icon?: React.ComponentProps<typeof Feather>['name'] | null;
}

/**
 * Terracotta gradient pill — the single strong CTA. Disabled collapses to a
 * flat muted fill with no shadow (matches the handoff's disabled "Continuer").
 */
export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  label,
  onPress,
  disabled = false,
  loading = false,
  icon = 'arrow-right',
}) => {
  const isInactive = disabled || loading;

  const content = (
    <View style={styles.row}>
      {loading ? (
        <ActivityIndicator color="#fbf9f6" />
      ) : (
        <>
          <Text style={[styles.label, isInactive && styles.labelDisabled]}>{label}</Text>
          {icon ? (
            <Feather name={icon} size={18} color={isInactive ? '#a3948a' : '#fbf9f6'} />
          ) : null}
        </>
      )}
    </View>
  );

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isInactive}
      activeOpacity={0.85}
      style={styles.touch}
    >
      {isInactive ? (
        <View style={[styles.pill, styles.pillDisabled]}>{content}</View>
      ) : (
        <LinearGradient
          colors={['#a36642', Paprika.accent]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={[styles.pill, styles.pillShadow]}
        >
          {content}
        </LinearGradient>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  touch: { width: '100%' },
  pill: {
    height: 52,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillShadow: {
    ...Platform.select({
      ios: {
        shadowColor: '#864e2c',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
      },
      android: { elevation: 4 },
      default: {
        boxShadow: '0 6px 16px -4px rgba(134,78,44,0.4), inset 0 1px 0 rgba(255,255,255,0.18)',
      },
    }),
  },
  pillDisabled: {
    backgroundColor: '#e7ddd3',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
  },
  label: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    letterSpacing: 0.15,
    color: '#fbf9f6',
  },
  labelDisabled: {
    color: '#a3948a',
  },
});
